import React, { useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Add, EmojiEvents, VisibilityOutlined } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { unpackToDateTime } from '@campfire/hot-date';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useEndpointFetch } from '../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { Badge } from '../../../../../common/badges/Badge';
import { BadgeDesign, DEFAULT_BADGE_DESIGN } from '../../../../../common/badges/badge-svg';
import { GET_VOLUNTEER_ACHIEVEMENTS } from './volunteer-achievements-section.gql';
import {
  GetVolunteerAchievements,
  GetVolunteerAchievementsVariables,
  GetVolunteerAchievements_vm_volunteer_awardedAchievements as Award,
} from './__generated__/GetVolunteerAchievements';
import { AwardAchievementDialog } from './AwardAchievementDialog';

const BADGE_SIZE = 56;

const useStyles = makeStyles(() =>
  createStyles({
    group: { '& + &': { marginTop: '24px' } },
    groupName: { fontWeight: 600, fontSize: '15px', color: '#333' },
    levels: { display: 'flex', flexWrap: 'wrap', gap: '18px', marginTop: '10px' },
    level: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '6px',
      padding: '6px',
      width: '92px',
      outline: 'none',
    },
    levelName: { fontSize: '13px', fontWeight: 600, color: '#333', textAlign: 'center', lineHeight: 1.25 },
    levelDate: { fontSize: '12px', fontWeight: 400, color: '#767676', textAlign: 'center', lineHeight: 1.25 },
    faded: { filter: 'grayscale(1)', opacity: 0.32 },
    noBadge: {
      width: `${BADGE_SIZE}px`,
      height: `${BADGE_SIZE}px`,
      borderRadius: '50%',
      background: '#F2F2F2',
      color: '#9E9E9E',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    empty: { fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' },
  })
);

interface Props {
  volunteerId: string;
  canManage: boolean;
}

const METRIC_UNITS = new Map([
  ['hours', 'hours'],
  ['years_of_service', 'years'],
  ['sessions_attended', 'sessions'],
]);

const parseDesign = (json: string | null): BadgeDesign | null => {
  if (!json) return null;
  try {
    return { ...DEFAULT_BADGE_DESIGN, ...JSON.parse(json) };
  } catch (e) {
    return null;
  }
};

const formatDate = (value: string) => unpackToDateTime(value).toFormat('d MMM yyyy');

const personName = (person: Award['awardedBy']) =>
  person ? `${person.profile.preferredName} ${person.profile.lastName}` : null;

const describeAward = (award: Award) => {
  if (award.isRevoked && award.revokedAt) {
    const by = personName(award.revokedBy);
    return `Revoked ${formatDate(award.revokedAt)}${by ? ` by ${by}` : ''}`;
  }
  const parts = [`Awarded ${formatDate(award.awardedAt)}`];
  if (award.source === 'auto') parts.push('Automatic');
  if (award.source === 'backfill') parts.push('Backfilled');
  if (award.source === 'manual')
    parts.push(personName(award.awardedBy) ? `By ${personName(award.awardedBy)}` : 'Manual');
  if (award.valueAtAward !== null) {
    const unit = METRIC_UNITS.get(award.level.achievement.metric);
    parts.push(`${Number(award.valueAtAward.toFixed(1))}${unit ? ` ${unit}` : ''}`);
  }
  return parts.join(' · ');
};

export const VolunteerAchievementsSection = ({ volunteerId, canManage }: Props) => {
  const classes = useStyles();
  const {
    volunteerNameOptions: { preferredName },
  } = useVolunteerCommonProfileContext();
  const { setSnackbar } = useSnackbar();
  const { data, refetch } = useCampfireQuery<GetVolunteerAchievements, GetVolunteerAchievementsVariables>(
    GET_VOLUNTEER_ACHIEVEMENTS,
    { options: { variables: { volunteerId } } }
  );
  const awardFetch = useEndpointFetch<{ volunteerIds: string[]; achievementLevelId: string; note: string | null }>(
    '/vm/admin-console/achievement/award'
  );
  const [awardOpen, setAwardOpen] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const { theme } = useCampfireTheme();

  const awards = data?.vm.volunteer?.awardedAchievements ?? [];
  const awardsByLevel = useMemo(() => new Map(awards.map((item) => [item.level.achievementLevelId, item])), [awards]);
  const heldLevelIds = useMemo(
    () => awards.filter((item) => !item.isRevoked).map((item) => item.level.achievementLevelId),
    [awards]
  );
  const achievements = useMemo(
    () =>
      (data?.vm.achievements ?? [])
        .filter((achievement) =>
          viewAll
            ? achievement.isEnabled || achievement.levels.some((level) => awardsByLevel.has(level.achievementLevelId))
            : achievement.levels.some((level) => heldLevelIds.includes(level.achievementLevelId))
        )
        .map((achievement) => ({
          ...achievement,
          levels: [...achievement.levels].sort((a, b) => a.position - b.position),
        })),
    [data, awardsByLevel, heldLevelIds, viewAll]
  );

  const award = (achievementLevelId: string, note: string) =>
    awardFetch
      .run({ volunteerIds: [volunteerId], achievementLevelId, note: note.trim() || null })
      .then((response) => {
        if (!response.ok) throw new Error(response.data?.message);
        setSnackbar({ open: true, variant: 'success', message: 'Level awarded' });
        setAwardOpen(false);
        if (refetch) setTimeout(refetch, 0);
      })
      .catch((e) => setSnackbar({ open: true, variant: 'error', message: e?.message || 'Unable to award level' }));

  return (
    <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
      <Grid item xs={12} style={{ textAlign: 'left' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography
              style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
            >
              <EmojiEvents style={{ fontSize: '14px', paddingRight: '5px' }} /> Achievements
            </Typography>
            <Typography variant='body2' style={{ color: '#464646' }} align='left'>
              Achievement levels {preferredName} has been awarded
            </Typography>
          </Box>
          <Box display='flex' alignItems='flex-start'>
            <Button
              variant='outlined'
              startIcon={<VisibilityOutlined />}
              style={{
                height: 36,
                borderRadius: 6,
                border: '1px solid #DCDCDC',
                color: theme?.color?.grey?.neutral400,
                textTransform: 'capitalize',
                marginTop: '4px',
              }}
              onClick={() => setViewAll(!viewAll)}
            >
              <Typography style={{ fontWeight: 600, fontSize: '13px', lineHeight: '19px', whiteSpace: 'nowrap' }}>
                {`Viewing: ${viewAll ? 'All' : 'Awarded'}`}
              </Typography>
            </Button>
            {canManage && (
              <TabletButton
                size='small'
                variant='contained'
                color='primary'
                onClick={() => setAwardOpen(true)}
                startIcon={<Add />}
                style={{
                  height: 36,
                  border: '1px solid transparent',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  marginRight: '10px',
                  marginLeft: '10px',
                  marginTop: '4px',
                  color: '#fff',
                }}
              >
                Add
              </TabletButton>
            )}
          </Box>
        </Box>
        <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />

        {achievements.map((achievement) => (
          <Box key={achievement.achievementId} className={classes.group}>
            <Typography className={classes.groupName}>{achievement.name}</Typography>
            <Box className={classes.levels}>
              {achievement.levels.map((level) => {
                const levelAward = awardsByLevel.get(level.achievementLevelId);
                const held = Boolean(levelAward && !levelAward.isRevoked);
                const design = parseDesign(level.badgeDesign);
                return (
                  <TitularTooltip
                    key={level.achievementLevelId}
                    title={level.name}
                    description={[
                      achievement.name,
                      levelAward ? describeAward(levelAward) : 'Not awarded yet',
                      levelAward?.note,
                    ]
                      .filter(Boolean)
                      .join('\n')}
                    maxWidth={240}
                  >
                    <Box className={classes.level} tabIndex={0}>
                      <Box className={held ? undefined : classes.faded} lineHeight={0}>
                        {design ? (
                          <Badge design={design} size={BADGE_SIZE} label={level.name} />
                        ) : (
                          <Box className={classes.noBadge}>
                            <EmojiEvents />
                          </Box>
                        )}
                      </Box>
                      <Typography className={classes.levelName}>{level.name}</Typography>
                      {held && levelAward && (
                        <Typography className={classes.levelDate}>{formatDate(levelAward.awardedAt)}</Typography>
                      )}
                    </Box>
                  </TitularTooltip>
                );
              })}
            </Box>
          </Box>
        ))}

        {data && achievements.length === 0 && (
          <Typography variant='body2' className={classes.empty}>
            {viewAll ? 'No achievements set up yet' : `${preferredName} hasn't been awarded any achievements yet`}
          </Typography>
        )}
      </Grid>

      {canManage && (
        <AwardAchievementDialog
          open={awardOpen}
          volunteerName={preferredName}
          achievements={data?.vm.achievements ?? []}
          heldLevelIds={heldLevelIds}
          saving={awardFetch.isLoading}
          onClose={() => setAwardOpen(false)}
          onAward={award}
        />
      )}
    </Grid>
  );
};
